"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, RefreshCw, Flame, Zap, ShieldCheck, PhoneCall, ArrowRight, Clock, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface WaterHeaterServiceItem {
  id: string;
  title: string;
  tagline: string;
  description: string;
  iconName: "Wrench" | "RefreshCw" | "Flame" | "Zap" | "ShieldCheck";
  urgency: string;
  points: string;
  link: string;
}
const serviceItems: WaterHeaterServiceItem[] = [{
  id: "repair",
  title: "Water Heater Repair",
  tagline: "Same-Day Diagnostic & Fix",
  description: "Rapid troubleshooting for leaking tanks, faulty heating elements, broken thermocouples, and pilot light failures.",
  iconName: "Wrench",
  urgency: "Available 24/7",
  points: "No hot water • Tank leaks • Strange rumbling noises • Tripped breakers",
  link: "/water-heater-repair/"
}, {
  id: "replacement",
  title: "Water Heater Replacement",
  tagline: "Seamless Upgrades & Safe Removal",
  description: "Full-service replacement of aging, corroded, or burst water heaters with high-efficiency modern units sized for your home.",
  iconName: "RefreshCw",
  urgency: "Same-Day Installs",
  points: "Tanks aged 10+ years • Severe bottom rust • Inefficient energy use",
  link: "/water-heater-replacement/"
}, {
  id: "tankless",
  title: "Tankless Water Heater Service",
  tagline: "Endless Hot Water & Descaling",
  description: "Specialized maintenance, annual descaling, and performance repairs for high-efficiency on-demand tankless systems.",
  iconName: "Flame",
  urgency: "High Efficiency",
  points: "Endless hot water • Compact wall mount • Annual descaling flushes",
  link: "/tankless-water-heater-service/"
}, {
  id: "gas",
  title: "Gas Water Heater Service",
  tagline: "Burner, Pilot & Venting Solutions",
  description: "Certified repairs and safety diagnostics for natural gas and propane water heaters, pilot assemblies, and flue exhaust lines.",
  iconName: "Flame",
  urgency: "Certified Gas Care",
  points: "Pilot assembly • Gas control valves • Safe exhaust ventilation",
  link: "/gas-water-heater-service/"
}, {
  id: "electric",
  title: "Electric Water Heater Service",
  tagline: "Elements & Thermostat Repairs",
  description: "Fast electrical diagnostics and replacements for dual heating elements, upper and lower thermostats, and wiring junctions.",
  iconName: "Zap",
  urgency: "Fast Diagnostics",
  points: "Heating elements • Dual thermostats • Electrical breaker checks",
  link: "/electric-water-heater-service/"
}];
export default function WaterHeaterServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (name: string) => {
    switch (name) {
      case "Wrench":
        return <Wrench className="size-6 text-primary" />;
      case "RefreshCw":
        return <RefreshCw className="size-6 text-primary" />;
      case "Flame":
        return <Flame className="size-6 text-primary" />;
      case "Zap":
        return <Zap className="size-6 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-6 text-primary" />;
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="water-heater-services" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs md:text-sm font-semibold tracking-wide uppercase mb-4 border border-border">
            <Flame className="size-4 text-accent" />
            <span>Jonesboro Water Heater Specialists</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-extrabold tracking-tight text-foreground leading-tight">
            Complete Water Heater Repair, Replacement & Care
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From sudden ice-cold showers and basement flooding to scheduled tankless upgrades, our licensed Jonesboro technicians respond fast with clear pricing and permanent solutions.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {serviceItems.map((service, i) => <motion.div key={service.id} data-index={i} variants={itemVariants} className="flex">
              <Card className="flex flex-col justify-between w-full bg-card border-border border rounded-xl hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 overflow-hidden relative">
                <div className="h-1.5 w-full bg-accent" />
                
                <CardHeader className="p-6 pb-2">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center border border-border">
                      {renderIcon(service.iconName)}
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
                      <Clock className="size-3" />
                      {service.urgency}
                    </span>
                  </div>

                  <CardTitle className="text-xl font-bold font-default text-foreground">
                    {service.title}
                  </CardTitle>
                  <p className="text-xs font-medium text-accent-foreground/80 mt-0.5">
                    {service.tagline}
                  </p>
                </CardHeader>

                <CardContent className="px-6 py-3 flex-1 flex flex-col justify-between">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {service.description}
                  </CardDescription>

                  <div className="p-3 rounded-lg bg-background border border-border">
                    <p className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                      <CheckCircle2 className="size-3.5 text-primary" />
                      Key Diagnostics & Scope:
                    </p>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {service.points}
                    </p>
                  </div>
                </CardContent>

                <CardFooter className="p-6 pt-2">
                  <Button asChild variant="outline" className="w-full justify-between rounded-full border-border hover:bg-primary hover:text-primary-foreground font-semibold group">
                    <Link to={service.link}>
                      <span>Schedule Service</span>
                      <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Immediate Triage Emergency Strip */}
        <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-secondary border border-border flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 shadow-xs">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-destructive text-destructive-foreground shrink-0 mt-1">
              <AlertTriangle className="size-6" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold font-default text-foreground">
                Is Your Water Heater Actively Leaking or Flooding?
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Turn off your water heater’s cold water shut-off valve and gas or power breaker immediately. Call our 24/7 Jonesboro dispatch team for immediate emergency response.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold rounded-full px-6 py-6 shadow-sm flex-1 sm:flex-none justify-center">
              <a href="tel:(470)847-9076">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-6 font-semibold flex-1 sm:flex-none justify-center">
              <Link to="/contact/">
                <span>Book Priority Visit</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}